import { Permission } from '@bookora/shared';

import type { TourDef } from './types';

/**
 * `/calendar`. `calendar.board` is on the populated grid only and
 * `calendar.board-empty` on the empty-board state (doctor view: «هنوز پزشکی
 * … تعریف نشده»; room view: «هنوز اتاقی …»), so exactly one of the two board
 * steps runs (critic C8). The chip never auto-offers an empty board; a manual
 * start explains why the board is empty instead of promising empty slots.
 *
 * Version 2: `board-empty` and `waitlist` joined the step set (one bump for
 * both, critic C9). The waitlist step promises a place in the free-slot SMS
 * queue, not an SMS (critic C22): a cancellation notifies matching entries,
 * a bounded number at a time.
 */
export const CALENDAR_TOUR: TourDef = {
  key: 'calendar',
  version: 2,
  surface: 'page',
  title: 'تقویم نوبت‌ها',
  hubDescription:
    'جابه‌جایی بین روزها، نمای روز و هفته، ثبت نوبت، درخواست‌های آنلاین و فهرست انتظار.',
  offer: { body: 'در چند قدم کوتاه نشونتون می‌دیم نوبت‌ها چطور دیده، ثبت و جابه‌جا می‌شن.' },
  route: { path: '/calendar', match: (pathname) => pathname === '/calendar' },
  requires: { all: [Permission.APPOINTMENT_VIEW] },
  finish: {
    label: 'ثبت نوبت جدید',
    // `?new=1` opens the booking form (calendar `hooks.ts` newBooking).
    to: '/calendar?new=1',
    requires: { all: [Permission.APPOINTMENT_CREATE] },
  },
  autoOffer: true,
  maxSteps: 5,
  minSteps: 2,
  // The two board steps never both run; the waitlist is the first to go.
  priority: ['nav', 'board', 'board-empty', 'filters', 'requests', 'waitlist'],
  steps: [
    {
      id: 'nav',
      anchor: 'calendar.nav',
      placement: 'bottom-start',
      title: 'جابه‌جایی بین روزها',
      body: 'با این دکمه‌ها روز یا هفتهٔ قبل و بعد رو ببینید. برای برگشتن به تاریخ امروز، «امروز» رو بزنید.',
    },
    {
      id: 'filters',
      anchor: { wide: 'calendar.filters', compact: 'calendar.filters-compact' },
      placement: 'bottom-end',
      title: 'نما و فیلترها',
      body: (ctx) =>
        ctx.viewport === 'compact'
          ? 'نمای روز یا هفته، پزشک، اتاق و وضعیت نوبت‌ها رو از اینجا انتخاب کنید.'
          : 'روز یا هفته رو انتخاب کنید و در نمای روز، ستون‌ها رو بر اساس پزشک یا اتاق بچینید. فیلتر پزشک و وضعیت نوبت هم همین‌جاست.',
    },
    {
      id: 'board',
      anchor: 'calendar.board',
      placement: 'auto',
      title: (ctx) => {
        if (!ctx.can(Permission.APPOINTMENT_CREATE)) return 'نوبت‌ها روی جدول';
        return ctx.viewport === 'compact' ? 'ثبت نوبت جدید' : 'ثبت نوبت از تقویم';
      },
      body: (ctx) => {
        if (!ctx.can(Permission.APPOINTMENT_CREATE)) {
          return 'رنگ هر نوبت وضعیتش رو نشون می‌ده. با زدن روی نوبت، جزئیاتش باز می‌شه.';
        }
        if (ctx.viewport === 'compact')
          return 'روی یک ساعت خالی بزنید تا فرم ثبت نوبت برای همون ساعت باز بشه.';
        const create = 'روی یک ساعت خالی کلیک کنید تا فرم ثبت نوبت برای همون ساعت باز بشه.';
        return ctx.can(Permission.APPOINTMENT_EDIT)
          ? `${create} برای جابه‌جایی، نوبت رو بکشید و در ساعت جدید رها کنید.`
          : create;
      },
    },
    {
      id: 'board-empty',
      anchor: 'calendar.board-empty',
      placement: 'auto',
      title: 'جدول هنوز خالی است',
      // Doctor view or room view — the tour cannot tell which: the doctor
      // empty state's button is «دعوت پزشک» (staff.invite), the room one's
      // «تعریف اتاق» (settings.edit), and each description otherwise says
      // to ask the manager. Only a viewer with both keys has a button either
      // way; one key alone may be looking at the view without it.
      body: (ctx) => {
        const lead = 'هر ستون این جدول یک پزشک است، یا در نمای «اتاق‌ها» یک اتاق.';
        const invite = ctx.can(Permission.STAFF_INVITE);
        const rooms = ctx.can(Permission.SETTINGS_EDIT);
        if (invite && rooms) {
          return `${lead} هنوز ستونی برای تقویم تعریف نشده. روش اضافه کردن ستون رو در همین کادر ببینید.`;
        }
        if (invite || rooms) {
          return `${lead} هنوز ستونی برای تقویم تعریف نشده. راهنمای همین کادر رو ببینید.`;
        }
        return ctx.can(Permission.APPOINTMENT_CREATE)
          ? `${lead} هنوز ستونی ساخته نشده؛ از مدیر کلینیک بخواید پزشکان رو اضافه کنه. تا اون موقع، نوبت بدون پزشک هم ثبت می‌شه.`
          : `${lead} هنوز ستونی ساخته نشده؛ از مدیر کلینیک بخواید پزشکان رو اضافه کنه.`;
      },
    },
    {
      id: 'requests',
      anchor: 'calendar.tab-requests',
      placement: 'bottom',
      requires: { all: [Permission.APPOINTMENT_CONFIRM_REQUESTS] },
      title: 'درخواست‌های آنلاین',
      body: 'درخواست‌های نوبتی که بیماران از سایت یا اپ مراجعان می‌فرستن، اینجا منتظر بررسی شما هستن. عدد کنار تب، تعداد درخواست‌های بررسی‌نشده است.',
    },
    {
      id: 'waitlist',
      anchor: 'calendar.tab-waitlist',
      placement: 'bottom',
      requires: { all: [Permission.APPOINTMENT_CREATE] },
      title: 'فهرست انتظار',
      body: 'اگر زمان خالی ندارید، بیمار رو به فهرست انتظار اضافه کنید. با لغو نوبت، اگر زمانش برای بیمار مناسب باشه، بیمار در صف پیامک زمان آزاد قرار می‌گیره.',
    },
  ],
};
